import groupBy from 'lodash/groupBy'
import { GetServerSidePropsContext } from 'next'
import getConfig from 'next/config'
import Link from 'next/link'
import { FiArrowRight } from 'react-icons/fi'
import { getNewNeighborhoods } from 'src/data/Neighborhoods'

import Layout from '@components/layouts'
import { Neighborhood } from '@openigloo/common'
import { titleCase } from '@utility/Utilities'

const BOROUGH_NAME_MAP: Record<string, string> = {
  manhattan: 'Manhattan',
  brooklyn: 'Brooklyn',
  queens: 'Queens',
  'the-bronx': 'The Bronx',
  'staten-island': 'Staten Island',
}

function BuildingsNeighborhood({
  neighborhoods,
  borough,
}: {
  neighborhoods: Neighborhood[]
  borough: string
}) {
  const { publicRuntimeConfig } = getConfig()
  const canonicalURL = `${publicRuntimeConfig.SITE_BASE_URL}/buildings/borough/${borough}`

  // Remove neighborhoods with no borough
  const filteredNeighborhoods = neighborhoods.filter((n) => n.borough)

  // Group by borough
  const groupedBoroughs = groupBy(filteredNeighborhoods, (nbr) => nbr.borough)

  // Normalize nested grouping: borough → first letter
  const totalNbrs: Record<string, number> = {}
  Object.keys(groupedBoroughs).forEach((br) => {
    totalNbrs[br] = groupedBoroughs[br].length
    const groupedByLetter = groupBy(groupedBoroughs[br], (nbr) => nbr.name[0])
    // @ts-expect-error: groupBy typing limitation
    groupedBoroughs[br] = groupedByLetter
  })

  // Resolve the real borough name
  const normalizedBorough = BOROUGH_NAME_MAP[borough.toLowerCase()]
  const neighborhoodsByLetter = groupedBoroughs[normalizedBorough] || {}

  return (
    <Layout title={`Neighborhoods in ${titleCase(borough, '-')}`} canonicalURL={canonicalURL}>
      <div className="mx-auto my-6 max-w-screen-lg px-4 pb-24 md:pb-6 lg:px-0">
        <div className="flex items-center space-x-2 text-xs text-dark-700">
          <Link href={'/buildings'}>NYC Buildings</Link>
          <div>/</div>
          <div>{titleCase(borough, '-')}</div>
        </div>

        <h1 className="pt-6 text-2xl font-semibold text-dark-900">
          {totalNbrs[normalizedBorough] || 0} Neighborhoods in {titleCase(borough, '-')}
        </h1>

        <div className="my-8 grid gap-6 sm:grid-cols-2">
          {Object.keys(neighborhoodsByLetter)
            .sort()
            .reverse()
            .map((letter) => (
              <div key={`heading-${letter}`} className="border-b py-2">
                <div className="w-full pb-2 text-xl font-semibold text-dark-900">{letter}</div>
                <div className="grid grid-cols-2">
                  {neighborhoodsByLetter[letter].map((nbr) => (
                    <div key={nbr.name} className="my-2">
                      <a href={`/buildings/${nbr.slug}`} className="text-dark-700">
                        {nbr.name}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>

        <h2 className="text-xl font-semibold text-dark-900">Other Boroughs in NYC</h2>
        <div className="my-8 grid gap-4 sm:grid-cols-2 sm:gap-6">
          {Object.keys(groupedBoroughs).map((br) => {
            const slug = br.toLowerCase().split(' ').join('-')
            return (
              br !== normalizedBorough && (
                <Link href={`/buildings/borough/${slug}`} key={br}>
                  <a className="rounded-[5px] bg-light-20 px-4 py-3">
                    <div className="flex items-center space-x-2 font-semibold text-dark-900">
                      <h2>{br}</h2>
                      <FiArrowRight color="#061938" size={24} />
                    </div>
                    <div className="pt-2 text-sm text-dark-500">{totalNbrs[br]} neighborhoods</div>
                  </a>
                </Link>
              )
            )
          })}
        </div>
      </div>
    </Layout>
  )
}

export async function getServerSideProps({ params }: GetServerSidePropsContext) {
  const { borough } = params

  try {
    const [neighborhoods] = await Promise.all([getNewNeighborhoods('New York')])

    // Redirect old slug to correct one
    if (borough === 'bronx') {
      return {
        redirect: {
          permanent: true,
          destination: `/buildings/borough/the-bronx`,
        },
        props: {},
      }
    }

    const validBoroughSlugs = ['manhattan', 'queens', 'brooklyn', 'the-bronx', 'staten-island']
    if (!validBoroughSlugs.includes(borough.toString().toLowerCase())) {
      return {
        redirect: {
          permanent: false,
          destination: `/buildings`,
        },
        props: {},
      }
    }

    return {
      props: {
        neighborhoods,
        borough,
      },
    }
  } catch {
    return {
      redirect: {
        permanent: false,
        destination: '/404',
      },
      props: {},
    }
  }
}

export default BuildingsNeighborhood
